import * as THREE from 'three'
import { extend } from '@react-three/fiber'
import glsl from 'babel-plugin-glsl/macro'

function getPoint(v, size, data, offset) {
  v.set(Math.random() * 2 - 1, Math.random() * 2 - 1, Math.random() * 2 - 1)
  if (v.length() > 1) return getPoint(v, size, data, offset)
  return v.normalize().multiplyScalar(size).toArray(data, offset)
}

function getSphere(count, size, p = new THREE.Vector4()) {
  const data = new Float32Array(count * 4)
  for (let i = 0; i < count * 4; i += 4) getPoint(p, size, data, i)
  return data
}

class SimulationMaterial extends THREE.ShaderMaterial {
  constructor(size = 256) {
    const positionsTexture = new THREE.DataTexture(getSphere(size * size, 2.2), size, size, THREE.RGBAFormat, THREE.FloatType)
    positionsTexture.needsUpdate = true

    super({
      vertexShader: `varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }`,
      fragmentShader: glsl`
        uniform sampler2D positions;
        uniform float uDelta;
        uniform int uFromAttractor;
        uniform int uToAttractor;
        uniform float uTransition;

        varying vec2 vUv;

        vec3 lorenzField(vec3 p) {
          const float sigma = 10.0;
          const float rho = 28.0;
          const float beta = 2.6666667;

          // Convert the normalized display coordinates into Lorenz space.
          // The physical z-axis is displayed vertically.
          vec3 q = vec3(
            p.x * 10.0,
            p.z * 10.0,
            p.y * 10.0 + 25.0
          );

          vec3 velocity = vec3(
            sigma * (q.y - q.x),
            q.x * (rho - q.z) - q.y,
            q.x * q.y - beta * q.z
          );

          return vec3(
            velocity.x,
            velocity.z,
            velocity.y
          ) / 10.0 * 0.8;
        }

        vec3 fourWingField(vec3 p) {
          const float a = 0.2;
          const float b = -0.01;
          const float c = 1.0;
          const float d = -0.4;
          const float e = -1.0;
          const float f = -1.0;

          // Wang-Sun system, with its physical z-axis displayed vertically.
          vec3 q = vec3(p.x, p.z, p.y);

          vec3 velocity = vec3(
            a * q.x + c * q.y * q.z,
            b * q.x + d * q.y - q.x * q.z,
            e * q.z + f * q.x * q.y
          );

          return vec3(
            velocity.x,
            velocity.z,
            velocity.y
          ) * 4.0;
        }

        vec3 thomasField(vec3 p) {
          const float b = 0.208186;
          const float spatialScale = 2.75;
          const float timeScale = 10.0;

          // Thomas naturally spans roughly +/-1/b. Evaluate it in a larger
          // physical coordinate system so its displayed extent matches the
          // other normalized attractors. zxy keeps physical z vertical.
          vec3 velocity =
            sin(p.zxy * spatialScale) / spatialScale -
            b * p;

          return velocity * timeScale;
        }

        vec3 aizawaField(vec3 p) {
          const float alpha = 0.95;
          const float beta = 0.7;
          const float gamma = 0.6;
          const float delta = 3.5;
          const float zeta = 0.25;
          const float epsilon = 0.1;
          const float spatialScale = 1.2;
          const float verticalCenter = 0.7;
          const float timeScale = 1.0;

          // This system is also known as the Langford attractor. Center its
          // physical z-axis vertically, then map that axis to display-space y.
          vec3 q = vec3(
            p.x * spatialScale,
            p.z * spatialScale,
            p.y * spatialScale + verticalCenter
          );
          float radialSquared = q.x * q.x + q.y * q.y;

          vec3 velocity = vec3(
            (q.z - beta) * q.x - delta * q.y,
            delta * q.x + (q.z - beta) * q.y,
            gamma
              + alpha * q.z
              - q.z * q.z * q.z / 3.0
              - radialSquared * (1.0 + zeta * q.z)
              + epsilon * q.z * q.x * q.x * q.x
          );

          // Aizawa also has a stable equilibrium below the chaotic orbit.
          // This guard is zero throughout the measured attractor (z >= -0.37)
          // and only prevents transitioned particles from dying on that axis.
          float basinGuard =
            1.0 - smoothstep(-0.85, -0.45, q.z);
          velocity.x += 0.28 * basinGuard;
          velocity.z += 2.2 * basinGuard;

          return vec3(
            velocity.x,
            velocity.z,
            velocity.y
          ) / spatialScale * timeScale;
        }

        vec3 roesslerField(vec3 p) {
          const float a = 0.2;
          const float b = 0.2;
          const float c = 5.7;
          const float spatialScale = 13.0;
          const float timeScale = 2.0;
          const vec3 center = vec3(0.0, -0.9, 6.0);

          // Keep the Rössler spike vertical and center the spiral in the
          // normalized display volume.
          vec3 q = vec3(
            p.x * spatialScale + center.x,
            p.z * spatialScale + center.y,
            p.y * spatialScale + center.z
          );

          vec3 velocity = vec3(
            -q.y - q.z,
            q.x + a * q.y,
            b + q.z * (q.x - c)
          );

          return vec3(
            velocity.x,
            velocity.z,
            velocity.y
          ) / spatialScale * timeScale;
        }

        vec3 sprottBField(vec3 p) {
          const float spatialScale = 5.0;
          const float timeScale = 2.0;

          // Case B from Sprott's 1994 set of simple chaotic flows.
          // Its physical y-axis already works well as display-space vertical.
          vec3 q = p * spatialScale;
          vec3 velocity = vec3(
            q.y * q.z,
            q.x - q.y,
            1.0 - q.x * q.y
          );

          return velocity / spatialScale * timeScale;
        }

        vec3 dadrasField(vec3 p) {
          const float a = 3.0;
          const float b = 2.7;
          const float c = 1.7;
          const float d = 2.0;
          const float e = 9.0;
          const float spatialScale = 14.0;
          const float timeScale = 0.55;
          const vec3 center = vec3(1.0, -0.5, 0.0);

          // The physical z-axis is displayed vertically.
          vec3 q = vec3(
            p.x * spatialScale + center.x,
            p.z * spatialScale + center.y,
            p.y * spatialScale + center.z
          );

          vec3 velocity = vec3(
            q.y - a * q.x + b * q.y * q.z,
            c * q.y - q.x * q.z + q.z,
            d * q.x * q.y - e * q.z
          );

          return vec3(
            velocity.x,
            velocity.z,
            velocity.y
          ) / spatialScale * timeScale;
        }

        vec3 halvorsenField(vec3 p) {
          const float a = 1.89;
          const float spatialScale = 10.0;
          const float timeScale = 0.35;
          const vec3 center = vec3(-2.9);

          // Halvorsen is cyclically symmetric; this permutation simply keeps
          // its physical z-axis consistent with the other displayed fields.
          vec3 q = vec3(
            p.x * spatialScale + center.x,
            p.z * spatialScale + center.y,
            p.y * spatialScale + center.z
          );

          vec3 velocity = vec3(
            -a * q.x - 4.0 * q.y - 4.0 * q.z - q.y * q.y,
            -a * q.y - 4.0 * q.z - 4.0 * q.x - q.z * q.z,
            -a * q.z - 4.0 * q.x - 4.0 * q.y - q.x * q.x
          );

          return vec3(
            velocity.x,
            velocity.z,
            velocity.y
          ) / spatialScale * timeScale;
        }

        vec3 attractorFieldById(vec3 p, int attractorId) {
          if (attractorId == 1) return fourWingField(p);
          if (attractorId == 2) return thomasField(p);
          if (attractorId == 3) return aizawaField(p);
          if (attractorId == 4) return roesslerField(p);
          if (attractorId == 5) return sprottBField(p);
          if (attractorId == 6) return dadrasField(p);
          if (attractorId == 7) return halvorsenField(p);
          return lorenzField(p);
        }

        float phaseSpreadById(int attractorId) {
          if (attractorId == 1) {
            return 0.35;
          }

          if (attractorId == 3) {
            return 0.2;
          }

          if (attractorId == 5) {
            return 0.25;
          }

          if (
            attractorId == 4 ||
            attractorId == 6 ||
            attractorId == 7
          ) {
            return 0.2;
          }

          return 0.05;
        }

        vec3 attractorField(vec3 p, float transition) {
          return mix(
            attractorFieldById(p, uFromAttractor),
            attractorFieldById(p, uToAttractor),
            transition
          );
        }

        float particleSeed(vec2 uv) {
          return fract(
            sin(dot(uv, vec2(12.9898, 78.233)))
            * 43758.5453
          );
        }

        vec3 recoveryPosition(vec2 uv) {
          float azimuth =
            particleSeed(uv) * 6.2831853;
          float height =
            particleSeed(uv.yx + vec2(0.37, 0.11)) * 2.0 - 1.0;
          float radius =
            sqrt(max(0.0, 1.0 - height * height));

          return vec3(
            cos(azimuth) * radius,
            height,
            sin(azimuth) * radius
          ) * 0.25;
        }

        void main() {
          vec3 pos = texture2D(positions, vUv).rgb;

          float transition =
            smoothstep(0.0, 1.0, uTransition);

          // Midpoint integration is noticeably more stable than Euler for
          // chaotic systems while requiring only two field evaluations.
          // A persistent per-particle rate keeps particles distributed along
          // the same trajectories instead of synchronizing into filaments.
          float phaseSpread =
            mix(
              phaseSpreadById(uFromAttractor),
              phaseSpreadById(uToAttractor),
              transition
            );
          float phaseRate =
            mix(1.0 - phaseSpread, 1.0 + phaseSpread, particleSeed(vUv));
          float stepSize =
            min(uDelta, 0.033) * 0.3 * phaseRate;
          vec3 firstVelocity =
            attractorField(pos, transition);
          vec3 midpoint =
            pos + firstVelocity * stepSize * 0.5;
          vec3 secondVelocity =
            attractorField(midpoint, transition);

          pos += secondVelocity * stepSize;

          // The negated comparison catches both an excessive radius and NaN.
          if (!(dot(pos, pos) < 100.0)) {
            pos = recoveryPosition(vUv);
          }

          gl_FragColor = vec4(pos, 1.0);
        }
      `,
      uniforms: {
        positions: { value: positionsTexture },
        uDelta: { value: 0 },
        uFromAttractor: { value: 0 },
        uToAttractor: { value: 0 },
        uTransition: { value: 1 }
      }
    })
  }
}

extend({ SimulationMaterial })
